import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useGetConfigurationQuery, useUpdateModuleSettingsMutation } from '../services/api'
import { useOwner } from './OwnerContext'

export interface ModuleConfig {
  cte: boolean
  nfe: boolean
  cd: boolean
  logistics: boolean
  costs: boolean
  clients: boolean
}

interface ModuleConfigContextType {
  modules: ModuleConfig
  setModules: (modules: ModuleConfig) => void
  updateModule: (moduleKey: keyof ModuleConfig, enabled: boolean) => void
  isModuleEnabled: (moduleKey: keyof ModuleConfig) => boolean
  isLoading: boolean
  error: any
  refetch: () => void
}

const ModuleConfigContext = createContext<ModuleConfigContextType | undefined>(undefined)

export const useModuleConfig = () => {
  const context = useContext(ModuleConfigContext)
  if (context === undefined) {
    throw new Error('useModuleConfig must be used within a ModuleConfigProvider')
  }
  return context
}

interface ModuleConfigProviderProps {
  children: React.ReactNode
}

const defaultModules: ModuleConfig = {
  cte: true,
  nfe: true,
  cd: true,
  logistics: true,
  costs: true,
  clients: true
}

export const ModuleConfigProvider: React.FC<ModuleConfigProviderProps> = ({ children }) => {
  const [modules, setModulesState] = useState<ModuleConfig>(defaultModules)
  const { isOwnerValid } = useOwner()
  
  const { data: configData, isLoading, error, refetch } = useGetConfigurationQuery(undefined, {
    // Skip the query if owner is not valid yet
    skip: !isOwnerValid,
    // Don't refetch on window focus
    refetchOnFocus: false,
    // Don't refetch on reconnect
    refetchOnReconnect: false,
  })
  const [updateModuleSettings] = useUpdateModuleSettingsMutation()

  useEffect(() => {
    // Load module config from API or localStorage
    if (configData?.success && (configData.data as any)) {
      const dataAny: any = configData.data as any
      const fromSnake = dataAny.module_settings
      const fromCamel = dataAny.moduleSettings
      if (fromSnake) {
        setModulesState(fromSnake)
      } else if (fromCamel) {
        setModulesState(fromCamel)
      } else {
        // Fallback to localStorage
        const savedModules = localStorage.getItem('lognex-module-config')
        if (savedModules) {
          try {
            const parsedModules = JSON.parse(savedModules)
            setModulesState({ ...defaultModules, ...parsedModules })
          } catch (error) {
            // Error occurred
            setModulesState(defaultModules)
          }
        }
      }
    } else {
      // Fallback to localStorage
      const savedModules = localStorage.getItem('lognex-module-config')
      if (savedModules) {
        try {
          const parsedModules = JSON.parse(savedModules)
          setModulesState({ ...defaultModules, ...parsedModules })
        } catch (error) {
          // Error occurred
          setModulesState(defaultModules)
        }
      }
    }
  }, [configData])

  const setModules = useCallback(async (newModules: ModuleConfig) => {
    setModulesState(newModules)

    // Save to localStorage as backup
    localStorage.setItem('lognex-module-config', JSON.stringify(newModules))

    // Save to backend (with error handling)
    try {
      await updateModuleSettings(newModules).unwrap()
    } catch (error) {
      // Error occurred
      // Continue execution even if backend save fails
    }
  }, [updateModuleSettings])

  const updateModule = useCallback(async (moduleKey: keyof ModuleConfig, enabled: boolean) => {
    const newModules = { ...modules, [moduleKey]: enabled }
    await setModules(newModules)
  }, [modules, setModules])

  const isModuleEnabled = useCallback((moduleKey: keyof ModuleConfig): boolean => {
    return modules[moduleKey]
  }, [modules])

  return (
    <ModuleConfigContext.Provider value={{ 
      modules, 
      setModules, 
      updateModule, 
      isModuleEnabled,
      isLoading,
      error,
      refetch
    }}>
      {children}
    </ModuleConfigContext.Provider>
  )
}
